import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';

import { Button } from 'basic-components';
import { ArrowLeft, History, Play, Stop, Warning } from 'basic-components/icons';
import Query from 'dashboard-services/api/Query';
import { StatusesEnums, StatusesQuery } from 'dashboard-services/api/statuses';
import TimeService from 'dashboard-services/services/TimeService';
import { DashboardLinkFactory } from 'ng-common-features/urlBuilder';
import { Fetching, globalActions, StatusIcon, StatusStyleService } from 'primary-components';

import { statusesActions } from 'actions/modules/statuses';
import getApiConfig from 'api/ApiConfig';
import { pipelinesQueries } from 'queries/modules/datascience';
import { routing } from 'routing';

import RunTime from 'basic/RunTime';

import { useQuery } from '@tanstack/react-query';
import { When } from 'react-if';

import './StatusDetailsMobile.scss';

const onSelectStatus = response => {
  const items = response.items || []
  if(items[0] && !items?.[0]?.types){
    const oldType = items?.[0]?.type?.toLowerCase()
    items[0].types = { [oldType]: true }
  }
  if(items?.length > 0){
    return items?.[0]
  } else {
    return EMPTY_OBJ;
  }
}
const useGetStatus = ({ getQueryBuilder, jobId, jobIdParam, shouldBeLimited, isRefreshEnabled, type }) => useQuery({
  ...StatusesQuery.QUERIES.search(
    getQueryBuilder,
    {
      size: 1,
      query: "*",
      type: statusesActions.CATEGORIES[type.toUpperCase()],
      jobId: jobId ? jobId : jobIdParam
    }
  ),
  select: onSelectStatus,
  refetchInterval: () => shouldBeLimited() ? undefined : 5000,
  enabled: isRefreshEnabled
})

const EMPTY_OBJ = {};
const EMPTY_FUN = () => {}
const StatusDetailsMobile = () => {
  const dispatch = useDispatch(),
        navigate = useNavigate(),
        [searchParams] = useSearchParams(),
        userName = useSelector(state => state.myAccountState.settingsState.userName),
        definedTime = useSelector(state => state.myAccountState.settingsState.definedTime),
        { groupName, name, type, jobId } = useParams(),
        jobIdParam = searchParams.get("jobId") || jobId,
        fromHistory = searchParams.get("fromHistory"),
        getApiConfigLocal = useCallback(() => dispatch(getApiConfig()), [dispatch]),
        getQueryBuilder = useCallback(() => new Query.Builder(getApiConfigLocal()), [getApiConfigLocal]),
        getQueryBuilderWithoutFetching = useCallback(() => 
          new Query.Builder(getApiConfigLocal())
            .withFetching(false)
        , [getApiConfigLocal]),
        
        shouldBeLimited = useCallback(provider => dispatch(globalActions.shouldTheApiBeLimited(provider)), [dispatch]),
        [isRefreshEnabled, setIsRefreshEnabled] = useState(true),
        { data: status = EMPTY_OBJ, refetch: refetchStatuses, isPending } = useGetStatus({ getQueryBuilder: getQueryBuilderWithoutFetching, jobId, jobIdParam, shouldBeLimited, isRefreshEnabled, type }),
        isStopEnabled = useMemo(() => status?.status === StatusesEnums.STATUS.PAUSED || status?.status === StatusesEnums.STATUS.RUNNING || status?.status === StatusesEnums.STATUS.COMPLETED, [status]),
        isPlayEnabled = useMemo(() => status?.status !== StatusesEnums.STATUS.RUNNING && status?.status !== StatusesEnums.STATUS.PENDING && status?.status !== StatusesEnums.STATUS.UNEXECUTED && status?.status !== StatusesEnums.STATUS.PAUSED, [status]),
        changeUrljobId = useCallback(jobId => {
          navigate(DashboardLinkFactory.statuses.details({ baseUrl: "", groupName, jobId, name: type === routing.dataScience.readableName ? name : undefined, type }))
        },[groupName, name, navigate, type]),
        { mutateAsync: runPipelineManually } = pipelinesQueries.useRunPipeline({ pipelineId: status?.payload?.pipelineId, getQueryBuilder }),
        playPipeline = useCallback(() => 
          runPipelineManually()
            .then(res => {
              dispatch(globalActions.getMessageStore()).success("Pipeline has been run.")
              if(res?.jobId){
                changeUrljobId(res.jobId)
                refetchStatuses()
              }
            })
        , [changeUrljobId, dispatch, refetchStatuses, runPipelineManually]),
        navigateBack = useCallback(() => {
          if(fromHistory){
            navigate(DashboardLinkFactory.statuses.history({ baseUrl: "", groupName, name: type === routing.dataScience.readableName ? name : undefined, type }))
            return
          }
          navigate(DashboardLinkFactory.statuses.list({ baseUrl: "", type }))
        },[fromHistory, type, navigate, groupName, name]),
        navigateToHistory = useCallback(() => {
          navigate(DashboardLinkFactory.statuses.history({ baseUrl: "", groupName, name: name || undefined, type }))
        }, [groupName, name, navigate, type]),
        { mutateAsync: resumePipelineCall } = pipelinesQueries.useResumePipeline({ getQueryBuilder, jobId: jobId ? jobId : jobIdParam }),
        resumePipeline = useCallback(() =>
          resumePipelineCall({
            payload: {
              userName,
              changes: [{
                payload: {
                  action: "Approved via mobile website.",
                  approveTime: TimeService.getCurrentDate() + " " + TimeService.getCurrentTime(),
                  userName
                }
              }]
            }
          })
            .then(() => {
              dispatch(globalActions.getMessageStore()).success("Pipeline run has been continued.")
              refetchStatuses()
            })
        , [dispatch, refetchStatuses, resumePipelineCall, userName]),
        { mutateAsync: stopPipelineCall } = pipelinesQueries.useStopPipeline({ pipelineId: status?.payload?.pipelineId, getQueryBuilder, jobId: jobId ? jobId : jobIdParam }),
        stopPipeline = useCallback(() =>
          stopPipelineCall()
            .then(() => {
              dispatch(globalActions.getMessageStore()).success("Pipeline run has been terminated.")
              refetchStatuses()
            })
        , [dispatch, refetchStatuses, stopPipelineCall]),
        userFormatedDate = TimeService.getUserFormattedDateTime(status?.startTime, definedTime).split(" "),
        statusDescription = useMemo(() => {
          let statusLocal
          if(status?.types?.etl && !status?.types?.pipeline){
            statusLocal = status?.latestStatus?.payload?.fileName 
          } else {
            statusLocal = status.name + (status?.payload?.version ? " v." + status.payload.version : "")
          }
          return statusLocal
        }, [status]),
        timeoutRef = useRef()

  useEffect(() => {
    !jobIdParam && navigate(DashboardLinkFactory.statuses.list({ baseUrl: "", type }))
  }, [groupName, jobIdParam, navigate, type])

  useEffect(() => {
    const currentStatus = status?.status
    window.clearTimeout(timeoutRef.current)
    if(currentStatus && currentStatus !== StatusesEnums.STATUS.COMPLETED && currentStatus !== StatusesEnums.STATUS.ERROR && currentStatus !== StatusesEnums.STATUS.INFO){
      timeoutRef.current = window.setTimeout(() => setIsRefreshEnabled(false), 30_000)
    }
  }, [status?.status])

  return (
    <Fragment>
      <When condition={Object.values(status).length > 0}>
        {() => (
          <div className="statuses-details-view-mobile">
            <div className="statuses-details-view-mobile__header">
              <div 
                  className="statuses-details-view-mobile__header__button"
                  onClick={navigateBack}
              >
                <ArrowLeft
                    color="blue-bright"
                    height={32}
                    width={32}
                />
              </div>
              <div className="statuses-details-view-mobile__header__details">
                <div className="statuses-details-view-mobile__header__details__category">
                  {type === "Groups" ? "Group Status" : "Pipeline Status"}
                </div>
                <div className="statuses-details-view-mobile__header__details__group-name">
                  {status?.payload?.groupName || status?.latestStatus.payload.groupName || status?.name}
                </div>
              </div>
            </div>
            <div className="statuses-details-view-mobile__container">
              <When condition={status?.status === StatusesEnums.STATUS.PAUSED}>
                {() => (
                  <div className="statuses-details-view-mobile__container__resume">
                    <div className="statuses-details-view-mobile__container__resume__title">
                      <div className="statuses-details-view-mobile__container__resume__title__icon">
                        <Warning
                            bgColor="yellow-base"
                            color="white-base"
                            height={24}
                            width={24}
                        />
                      </div>
                      <div className="statuses-details-view-mobile__container__resume__title__text">
                        This pipeline requires approval
                      </div>
                    </div>
                    <div className="statuses-details-view-mobile__container__resume__buttons">
                      <Button 
                          onClick={stopPipeline}
                          variant="secondary" 
                      >
                        Decline
                      </Button>
                      <Button
                          onClick={resumePipeline}
                      >
                        Approve
                      </Button>
                    </div>
                  </div>
                )}
              </When>
              <When condition={!isPending && Object.values(status).length > 0}>
                {() => (
                  <div className="statuses-details-view-mobile__container__content">
                    <div 
                        className="statuses-details-view-mobile__container__content__summary"
                        style={{ borderLeftColor: StatusStyleService.getTagLabelStatusStyle(status.status).color }}
                      >
                      <div className="statuses-details-view-mobile__container__content__summary__status__title">
                        <div className="statuses-details-view-mobile__container__content__summary__status__title__icon">
                          <StatusIcon
                              status={status?.latestStatus?.status || status?.status}
                          />
                        </div>
                        <div className="statuses-details-view-mobile__container__content__summary__status__title__text">
                          {statusDescription}
                        </div>
                      </div>
                      <div className="statuses-details-view-mobile__container__content__summary__status__group">
                        {status?.payload?.groupName}
                      </div>
                      <div className="statuses-details-view-mobile__container__content__summary__status__container">
                        <div className="statuses-details-view-mobile__container__content__summary__status__container__details">
                          <div className="statuses-details-view-mobile__container__content__summary__status__container__details__start-time">
                            <span>
                              {String(userFormatedDate[0])}
                            </span>
                            <span>
                              {String(userFormatedDate[1])}
                            </span>
                          </div>
                          <div className="statuses-details-view-mobile__container__content__summary__status__container__details__duration">
                            Duration: {(
                            <RunTime
                                endTime={status?.endTime}
                                isRunning={status?.status === StatusesEnums.STATUS.RUNNING || status?.status === StatusesEnums.STATUS.PAUSED}
                                startTime={status?.startTime}
                            />
                          )}
                          </div>
                        </div>
                        <div className="statuses-details-view-mobile__container__content__summary__status__container__buttons">
                          <div 
                              className="statuses-details-view-mobile__container__content__summary__status__container__buttons__element"
                              onClick={navigateToHistory}
                          >
                            <History
                                color="blue-bright"
                                height={24}
                                width={24}
                            />
                          </div>
                          <When condition={status.type === StatusesEnums.STATUS_TYPE.PIPELINE}>
                            {() => (
                              <Fragment>
                                <div 
                                    className="statuses-details-view-mobile__container__content__summary__status__container__buttons__element"
                                    onClick={isStopEnabled ? stopPipeline : EMPTY_FUN}
                                >
                                  <Stop
                                      color={isStopEnabled ? "blue-bright" : "grey-base"}
                                      height={24}
                                      width={24}
                                  />
                                </div>
                                <div 
                                    className="statuses-details-view-mobile__container__content__summary__status__container__buttons__element"
                                    onClick={isPlayEnabled ? playPipeline : EMPTY_FUN}
                                >
                                  <Play
                                      color={isPlayEnabled ? "blue-bright" : "grey-base"}
                                      height={24}
                                      width={24}
                                  />
                                </div>
                              </Fragment>
                            )}
                          </When>
                        </div>
                      </div>
                    </div>
                    <div className="statuses-details-view-mobile__container__content__history">
                      {status?.componentStatuses?.map(status => (
                        <div 
                            className="statuses-details-view-mobile__container__content__history__element"
                            key={status?.eId}
                        >
                          <div className="statuses-details-view-mobile__container__content__history__element__title">
                            <div className="statuses-details-view-mobile__container__content__history__element__title__icon">
                              <StatusIcon
                                  status={status?.status}
                              />
                            </div>
                            <div className="statuses-details-view-mobile__container__content__history__element__title__component">
                              {status?.component}
                            </div>
                            <div className="statuses-details-view-mobile__container__content__history__element__title__duration">
                              <RunTime
                                  endTime={status?.endTime}
                                  isRunning={status?.status === StatusesEnums.STATUS.RUNNING || status?.status === StatusesEnums.STATUS.PAUSED}
                                  startTime={status?.startTime}
                              />
                            </div>
                          </div>
                          <div className="statuses-details-view-mobile__container__content__history__element__message">
                            {status?.message}
                          </div>
                          <div className="statuses-details-view-mobile__container__content__history__element__file-name">
                            {status?.payload?.fileName}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </When>
            </div>
          </div>
        )}
      </When>
      <When condition={isPending || Object.values(status).length === 0}>
        {() => (
          <div className="statuses-details-view-mobile">
            <Fetching isFetching/>
          </div>
        )}
      </When>
    </Fragment>
  )
}

export default StatusDetailsMobile;
